import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Input, Typography, theme, Tag, notification } from 'antd'
import { ErrorMessage } from '@hookform/error-message'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm, SubmitHandler, Controller } from 'react-hook-form'

import { clearPreAuthenInfo, getPreAuthenInfo, resetCountOtpSent, updatePreAuthenInfo } from 'src/app/store/preAuthen'
import { IDefaultResponse, IResetPassword } from 'src/app/shared/models/auth.model'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import dayjs from 'dayjs'

import '../shared/styles/index.scss'
import { UserOutlined, WarningFilled } from '@ant-design/icons'
import ErrorText from 'src/app/shared/components/common/error-text'
import { PasswordTooltip } from '../shared/components/password-tooltip'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import { PatternValidate } from 'src/app/shared/utils'
import { changePassword, checkNewPasswordValid } from 'src/app/shared/services/AuthenticationService'
import { FROM_RESET_PASSWORD_PAGE_STATE } from 'src/app/shared/constants/common.constants'
import { ResetPasswordType } from 'src/app/shared/models/common.model'
import { SERVER_STATUS_CODES } from 'src/app/shared/constants/serverResponseStatusCodes.constants'
import { IAuthState } from 'src/app/shared/models/store.model'
import { getUserProfile, logIn } from 'src/app/store/auth'
import { defaultHomePage } from 'src/app/shared/constants/app.constants'
import { getDeferredConfig } from 'src/app/store/masterSetup'
import './index.scss'

const { useToken } = theme

type TResetPasswordFormData = {
  userName: string
  currentPassword?: string //optional
  newPassword: string
  confirmNewPassword: string
}

const ResetPassword: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)
  const preAuthenInfo = useSelector(getPreAuthenInfo)
  const userProfile = useSelector(getUserProfile)
  const deferredConfig = useSelector(getDeferredConfig)

  const { state, pathname } = useLocation()

  const initForm: TResetPasswordFormData = {
    userName: '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  }

  const currentPasswordValidateRule: any = {
    required: {
      value: true,
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.BLANK_PASSWORD),
    },
    minLength: {
      value: deferredConfig?.systemSettings?.passwordMinimumLength,
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_IS_TOO_SHORT, {
        passwordMinimumLength: deferredConfig?.systemSettings?.passwordMinimumLength,
      }),
    },
    maxLength: {
      value: 128,
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_LONGER_THAN_128_CHAR),
    },
    validate: {
      hasSpecialChar: (value: string) =>
        PatternValidate.specialCharacterPattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NEW_PASSWORD_NO_SPECIAL_CHAR),
      hasAtLeastOneNumberPattern: (value: string) =>
        PatternValidate.atLeastOneNumberPattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_NO_NUMBER),
      hasAtLeastOneUppercasePattern: (value: string) =>
        PatternValidate.atLeastOneUppercasePattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_NO_UPPERCASE),
      hasAtLeastOneLowercasePattern: (value: string) =>
        PatternValidate.atLeastOneLowercasePattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_NO_LOWERCASE),
    },
  }

  const newPasswordValidateRule: any = {
    required: {
      value: true,
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.BLANK_PASSWORD),
    },
    minLength: {
      value: deferredConfig?.systemSettings?.passwordMinimumLength,
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_IS_TOO_SHORT, {
        passwordMinimumLength: deferredConfig?.systemSettings?.passwordMinimumLength,
      }),
    },
    maxLength: {
      value: 128,
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_LONGER_THAN_128_CHAR),
    },
    validate: {
      hasSpecialChar: (value: string) =>
        PatternValidate.specialCharacterPattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NEW_PASSWORD_NO_SPECIAL_CHAR),
      hasAtLeastOneNumberPattern: (value: string) =>
        PatternValidate.atLeastOneNumberPattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_NO_NUMBER),
      hasAtLeastOneUppercasePattern: (value: string) =>
        PatternValidate.atLeastOneUppercasePattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_NO_UPPERCASE),
      hasAtLeastOneLowercasePattern: (value: string) =>
        PatternValidate.atLeastOneLowercasePattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_NO_LOWERCASE),

      isSameWithCurrentPassword: (val: string) => {
        if (watch('currentPassword') == val) {
          return t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NEW_PASSWORD_SAME_AS_OLD)
        }
      },
    },
  }

  const confirmNewPasswordValidateRule: any = {
    required: {
      value: true,
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.BLANK_PASSWORD),
    },
    minLength: {
      value: deferredConfig?.systemSettings?.passwordMinimumLength,
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_IS_TOO_SHORT, {
        passwordMinimumLength: deferredConfig?.systemSettings?.passwordMinimumLength,
      }),
    },
    maxLength: {
      value: 128,
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_LONGER_THAN_128_CHAR),
    },
    validate: {
      hasSpecialChar: (value: string) =>
        PatternValidate.specialCharacterPattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NEW_PASSWORD_NO_SPECIAL_CHAR),
      hasAtLeastOneNumberPattern: (value: string) =>
        PatternValidate.atLeastOneNumberPattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_NO_NUMBER),
      hasAtLeastOneUppercasePattern: (value: string) =>
        PatternValidate.atLeastOneUppercasePattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_NO_UPPERCASE),
      hasAtLeastOneLowercasePattern: (value: string) =>
        PatternValidate.atLeastOneLowercasePattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_NO_LOWERCASE),

      isConfirmNewPasswordNotMatch: (val: string) => {
        if (watch('newPassword') != val) {
          return t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CONFIRMED_PASSWORD_DIFFERENT_FROM_NEW)
        }
      },
    },
  }

  useEffect(() => {
    dispatch(resetCountOtpSent())
    return () => {}
  }, [preAuthenInfo])

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
    setError,
    watch,
    setValue,
  } = useForm<IResetPassword>({ mode: 'all', defaultValues: initForm })

  const onSubmit: SubmitHandler<TResetPasswordFormData> = (data) => {
    const resetPasswordPayload: TResetPasswordFormData = {
      userName: data.userName,
      newPassword: data.newPassword,
      confirmNewPassword: data.confirmNewPassword,
    }

    if (data?.currentPassword) {
      resetPasswordPayload.currentPassword = data?.currentPassword
    }

    setLoading(true)

    if (
      state?.resetPasswordType == ResetPasswordType.FORCE_RESET ||
      state?.resetPasswordType == ResetPasswordType.FIRST_LOGIN ||
      state?.resetPasswordType == ResetPasswordType.FORGOT_PASSWORD
    ) {
      //force reset, first time reset password
      checkNewPasswordValid(resetPasswordPayload)
        .then(
          (res: any) => {
            dispatch(updatePreAuthenInfo(resetPasswordPayload))
            navigate('/login/otp', { state: { from: FROM_RESET_PASSWORD_PAGE_STATE } })
          },
          (err: IDefaultResponse<any>) => {
            handleErrorResponse(err)
          },
        )
        .finally(() => {
          setLoading(false)
        })
    } else if (state?.resetPasswordType == ResetPasswordType.MANUAL_RESET) {
      // manual change password
      changePassword(resetPasswordPayload)
        .then(
          (res: any) => {
            const dataAuth: IAuthState = res.data
            dispatch(clearPreAuthenInfo())
            dispatch(
              logIn({ ...dataAuth, accessTokenExpiresAt: dayjs().add(dataAuth.accessExpiresIn, 'seconds').format() }),
            )
            navigate(defaultHomePage)
            notification.success({
              message: <b>{t('common:success')}</b>,
              description: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.UPDATE_PASSWORD_SUCCESS),
            })
          },
          (err: IDefaultResponse<any>) => {
            handleErrorResponse(err)
          },
        )
        .finally(() => {
          setLoading(false)
        })
    }
  }

  useEffect(() => {
    if (state?.resetPasswordType === ResetPasswordType.MANUAL_RESET) {
      setValue('userName', userProfile?.userName, { shouldValidate: true, shouldDirty: true })
    }

    if (state?.userName) {
      setValue('userName', state?.userName, { shouldValidate: true, shouldDirty: true })
    }

    return () => {}
  }, [state])

  const handleErrorResponse = (err: IDefaultResponse<any>) => {
    switch (err?.statusCode) {
      case SYSTEM_MESSAGE_IDS.INCORRECT_CURRENT_PASSWORD:
        setError('currentPassword', {
          type: 'required',
          message: t('SystemMessages:' + err?.statusCode),
        })
        break

      case SYSTEM_MESSAGE_IDS.CONFIRMED_PASSWORD_DIFFERENT_FROM_NEW:
      case SYSTEM_MESSAGE_IDS.PASSWORD_LONGER_THAN_128_CHAR:
      case SYSTEM_MESSAGE_IDS.PASSWORD_NO_LOWERCASE:
      case SYSTEM_MESSAGE_IDS.PASSWORD_NO_UPPERCASE:
      case SYSTEM_MESSAGE_IDS.PASSWORD_NO_NUMBER:

      case SYSTEM_MESSAGE_IDS.NEW_PASSWORD_NO_SPECIAL_CHAR:
      case SYSTEM_MESSAGE_IDS.INCORRECT_USERNAME_PASSWORD:
        setError('confirmNewPassword', {
          type: 'required',
          message: t('SystemMessages:' + err?.statusCode),
        })
        break

      case SYSTEM_MESSAGE_IDS.PASSWORD_IS_TOO_SHORT:
        setError('confirmNewPassword', {
          type: 'required',
          message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_IS_TOO_SHORT, {
            passwordMinimumLength: deferredConfig?.systemSettings?.passwordMinimumLength,
          }),
        })
        break
      case SYSTEM_MESSAGE_IDS.NEW_PASSWORD_SAME_AS_OLD:
        setError('newPassword', {
          type: 'required',
          message: t('SystemMessages:' + err?.statusCode),
        })
        break

      case SERVER_STATUS_CODES.PPI1_USER_NOT_FOUND:
        setError('userName', {
          message: err?.message || 'error!',
          type: 'required',
        })
        break

      default:
        break
    }
  }

  return (
    <div className='login'>
      <div className='login__container'>
        <div style={{ textAlign: 'center' }}>
          <Typography.Text
            style={{
              fontSize: '32px',
              fontWeight: 400,
              lineHeight: '33px',
              color: '#7B858B',
              margin: 0,
              textAlign: 'center',
            }}
            className='title'
          >
            The most effective safeguard <br></br> against transaction fraud.
          </Typography.Text>
          <Typography.Text
            style={{
              display: 'block',
              fontWeight: 700,
              fontSize: '28px',
              lineHeight: '33px',
              margin: '8px 0 8px 0',
            }}
            className='reset-text'
          >
            {t('LoginPage:reset_password_title')}
          </Typography.Text>
        </div>
        <Tag className='tag-item' icon={<WarningFilled style={{ fontSize: '18px' }} />} color='warning'>
          <Typography.Text style={{ fontSize: '14px' }}>
            {state?.resetPasswordType === ResetPasswordType.FORCE_RESET
              ? t('LoginPage:force_reset_password_description_text')
              : t('LoginPage:manual_reset_password_description_text')}
          </Typography.Text>
        </Tag>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='login-input' style={{ marginBottom: '20px' }}>
            <Controller
              render={({ field }) => (
                <React.Fragment>
                  <Typography.Text
                    strong
                    style={{ marginBottom: 4, textAlign: 'left', fontSize: '14px' }}
                    className='required'
                  >
                    {t('LoginPage:user_name_input.label')}
                  </Typography.Text>
                  <Input
                    {...field}
                    allowClear
                    size='large'
                    status={errors.userName ? 'error' : ''}
                    autoComplete='user-name'
                    prefix={<UserOutlined />}
                    placeholder={t('LoginPage:user_name_input.placeholder')}
                    onPressEnter={handleSubmit(onSubmit)}
                    maxLength={225}
                  />
                </React.Fragment>
              )}
              name='userName'
              rules={{
                required: {
                  value: true,
                  message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.BLANK_USERNAME),
                },
              }}
              control={control}
            />
            <ErrorMessage errors={errors} name='userName' render={({ message }) => <ErrorText>{message}</ErrorText>} />
          </div>
          {state?.resetPasswordType === ResetPasswordType.FORCE_RESET ||
          state?.resetPasswordType === ResetPasswordType.MANUAL_RESET ? (
            <div className='login-input' style={{ marginBottom: '20px' }}>
              <Controller
                render={({ field }) => (
                  <React.Fragment>
                    <Typography.Text
                      strong
                      style={{ marginBottom: 4, textAlign: 'left', fontSize: '14px' }}
                      className='required'
                    >
                      {t('LoginPage:current_password_input.label')}
                    </Typography.Text>

                    <PasswordTooltip>
                      <Input.Password
                        {...field}
                        allowClear
                        size='large'
                        status={errors.currentPassword ? 'error' : ''}
                        autoComplete='current-password'
                        placeholder={t('LoginPage:current_password_input.placeholder')}
                        onPressEnter={handleSubmit(onSubmit)}
                        maxLength={128}
                      />
                    </PasswordTooltip>
                  </React.Fragment>
                )}
                rules={currentPasswordValidateRule}
                control={control}
                name='currentPassword'
              />
              <ErrorMessage
                errors={errors}
                name='currentPassword'
                render={({ message }) => <ErrorText>{message}</ErrorText>}
              />
            </div>
          ) : null}
          <div className='login-input' style={{ marginBottom: '20px' }}>
            <Controller
              render={({ field }) => (
                <React.Fragment>
                  <Typography.Text
                    strong
                    style={{ marginBottom: 4, textAlign: 'left', fontSize: '14px' }}
                    className='required'
                  >
                    {t('LoginPage:new_password_input.label')}
                  </Typography.Text>

                  <PasswordTooltip>
                    <Input.Password
                      {...field}
                      allowClear
                      size='large'
                      status={errors.newPassword ? 'error' : ''}
                      autoComplete='new-password'
                      placeholder={t('LoginPage:new_password_input.placeholder')}
                      onPressEnter={handleSubmit(onSubmit)}
                      maxLength={128}
                    />
                  </PasswordTooltip>
                </React.Fragment>
              )}
              rules={newPasswordValidateRule}
              control={control}
              name='newPassword'
            />
            <ErrorMessage
              errors={errors}
              name='newPassword'
              render={({ message }) => <ErrorText>{message}</ErrorText>}
            />
          </div>
          <div className='login-input' style={{ marginBottom: '40px' }}>
            <Controller
              render={({ field }) => (
                <React.Fragment>
                  <Typography.Text
                    strong
                    style={{ marginBottom: 4, textAlign: 'left', fontSize: '14px' }}
                    className='required'
                  >
                    {t('LoginPage:confirm_new_password_input.label')}
                  </Typography.Text>

                  <PasswordTooltip>
                    <Input.Password
                      {...field}
                      allowClear
                      size='large'
                      status={errors.confirmNewPassword ? 'error' : ''}
                      placeholder={t('LoginPage:confirm_new_password_input.placeholder')}
                      onPressEnter={handleSubmit(onSubmit)}
                      onChange={(e: any) => {
                        setValue('confirmNewPassword', e.target.value, { shouldValidate: true })
                      }}
                      maxLength={128}
                    />
                  </PasswordTooltip>
                </React.Fragment>
              )}
              rules={confirmNewPasswordValidateRule}
              control={control}
              name='confirmNewPassword'
            />
            <ErrorMessage
              errors={errors}
              name='confirmNewPassword'
              render={({ message }) => <ErrorText>{message}</ErrorText>}
            />
          </div>

          <Button
            size='large'
            onClick={handleSubmit(onSubmit)}
            type='primary'
            disabled={!isValid || loading}
            block
            loading={loading}
            style={{ marginBottom: '40px' }}
            className='reset-password-button'
          >
            {t('LoginPage:change_password_btn')}
          </Button>
        </form>
      </div>
    </div>
  )
}

export default ResetPassword
